import React, { useMemo, useState } from 'react';
import { PlanfactOperation } from '../services/api';
import {
  SortState,
  compareValues,
  formatContractSumDisplay,
  formatDateShort,
  toggleSort,
} from '../utils/table';
import { normalizeOperationAmount } from '../utils/operationBudgetArticle';

type TaskOpSortKey =
  | 'operation_date'
  | 'contractor_name_pf'
  | 'operation_type'
  | 'category_name'
  | 'comment'
  | 'amount'
  | 'account_type'
  | 'cash_amount'
  | 'tax_amount'
  | 'serv_fee_amount';

const OPERATION_TYPE_LABELS: Record<string, string> = {
  Income: '←',
  Outcome: '→',
  Move: '↔',
  Accrual: 'нач.',
  Shipment: 'отгр.',
  Supply: 'пост.',
};

const VISIBLE_BODY_ROWS = 20;

export interface TaskOpsExtraColumnVisibility {
  cash?: boolean;
  tax?: boolean;
  servFee?: boolean;
}

export interface TaskOperationsTableProps {
  operations: PlanfactOperation[];
  filterHint?: string | null;
  onClearFilter?: () => void;
  id?: string;
  extraColumns?: TaskOpsExtraColumnVisibility;
}

function isExtraColumnVisible(
  extraColumns: TaskOpsExtraColumnVisibility | undefined,
  key: keyof TaskOpsExtraColumnVisibility,
): boolean {
  if (!extraColumns) return true;
  return Boolean(extraColumns[key]);
}

function formatOperationType(value?: string): string {
  if (!value) return '—';
  return OPERATION_TYPE_LABELS[value] ?? value;
}

function renderOperationType(value?: string): React.ReactNode {
  const label = formatOperationType(value);
  if (value === 'Income') {
    return <span className="task-detail__ops-type task-detail__ops-type--income">{label}</span>;
  }
  if (value === 'Outcome') {
    return <span className="task-detail__ops-type task-detail__ops-type--outcome">{label}</span>;
  }
  return <span className="task-detail__ops-type">{label}</span>;
}

function formatNonZeroAmount(value: number): string {
  if (value === 0 || Number.isNaN(value)) return '';
  return formatContractSumDisplay(value);
}

function sortValue(op: PlanfactOperation, key: TaskOpSortKey): string | number | undefined {
  switch (key) {
    case 'operation_date':
      return op.operation_date;
    case 'contractor_name_pf':
      return op.contractor_name_pf;
    case 'operation_type':
      return op.operation_type;
    case 'category_name':
      return op.category_name;
    case 'comment':
      return op.comment;
    case 'amount':
      return normalizeOperationAmount(op.amount, op.operation_type);
    case 'account_type':
      return op.account_type;
    case 'cash_amount':
      return op.cash_amount ?? undefined;
    case 'tax_amount':
      return op.tax_amount ?? undefined;
    case 'serv_fee_amount':
      return op.serv_fee_amount ?? undefined;
    default:
      return undefined;
  }
}

function matchesSearch(op: PlanfactOperation, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const fields = [
    op.contractor_name_pf,
    op.category_name,
    op.comment,
    op.account_type,
    op.operation_type,
    formatOperationType(op.operation_type),
    op.budget_article,
  ];
  return fields.some((field) => field?.toLowerCase().includes(q));
}

const TaskOperationsTable: React.FC<TaskOperationsTableProps> = ({
  operations,
  filterHint,
  onClearFilter,
  id,
  extraColumns,
}) => {
  const showCash = isExtraColumnVisible(extraColumns, 'cash');
  const showTax = isExtraColumnVisible(extraColumns, 'tax');
  const showServFee = isExtraColumnVisible(extraColumns, 'servFee');
  const extrasLocked = extraColumns != null;
  const [searchText, setSearchText] = useState('');
  const [sortState, setSortState] = useState<SortState<TaskOpSortKey>>({
    key: 'operation_date',
    direction: 'desc',
  });

  const filteredSorted = useMemo(() => {
    const filtered = operations.filter((op) => {
      const amount = normalizeOperationAmount(op.amount, op.operation_type);
      const cash = Number(op.cash_amount) || 0;
      const tax = Number(op.tax_amount) || 0;
      const servFee = Number(op.serv_fee_amount) || 0;
      const hasVisibleExtras =
        (showCash && cash !== 0) || (showTax && tax !== 0) || (showServFee && servFee !== 0);
      if (amount === 0 && (extrasLocked ? !hasVisibleExtras : true)) {
        return false;
      }
      return matchesSearch(op, searchText);
    });
    return [...filtered].sort((left, right) =>
      compareValues(sortValue(left, sortState.key), sortValue(right, sortState.key), sortState.direction),
    );
  }, [operations, searchText, sortState, extrasLocked, showCash, showTax, showServFee]);

  const totals = useMemo(() => {
    let amount = 0;
    let cash = 0;
    let tax = 0;
    let servFee = 0;
    for (const op of filteredSorted) {
      amount += normalizeOperationAmount(op.amount, op.operation_type);
      if (showCash) cash += Number(op.cash_amount) || 0;
      if (showTax) tax += Number(op.tax_amount) || 0;
      if (showServFee) servFee += Number(op.serv_fee_amount) || 0;
    }
    return { amount, cash, tax, servFee, total: amount + cash + tax + servFee };
  }, [filteredSorted, showCash, showTax, showServFee]);

  const handleSort = (key: TaskOpSortKey): void => {
    setSortState((prev) => toggleSort(prev, key, key === 'operation_date' ? 'desc' : 'asc'));
  };

  const sortMark = (key: TaskOpSortKey): string => {
    if (sortState.key !== key) return '';
    return sortState.direction === 'asc' ? ' ↑' : ' ↓';
  };

  return (
    <div className="task-detail__ops-block" id={id}>
      <div className="task-detail__ops-head">
        <h2 className="task-detail__ops-title">Операции</h2>
        <div className="task-detail__ops-head-right">
          {filterHint ? (
            <div className="task-detail__ops-filter-hint">
              <span className="task-detail__ops-filter-text">{filterHint}</span>
              {onClearFilter ? (
                <button
                  type="button"
                  className="task-detail__ops-filter-clear"
                  onClick={onClearFilter}
                  aria-label="Сбросить фильтр"
                >
                  ×
                </button>
              ) : null}
            </div>
          ) : null}
          <input
            type="search"
            className="task-detail__ops-search"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Поиск…"
            aria-label="Поиск по операциям"
          />
        </div>
      </div>

      {filteredSorted.length === 0 ? (
        <p className="contractor-detail__muted">Нет операций для отображения.</p>
      ) : (
        <div
          className="task-detail__ops-wrap"
          style={{ ['--task-ops-visible-rows' as string]: VISIBLE_BODY_ROWS }}
        >
          <table className="task-detail__mini-table task-detail__mini-table--compact task-detail__mini-table--ops task-detail__ops-table">
            <colgroup>
              <col className="task-detail__ops-col-date" />
              <col className="task-detail__ops-col-contractor" />
              <col className="task-detail__ops-col-type" />
              <col className="task-detail__ops-col-category" />
              <col className="task-detail__ops-col-comment" />
              <col className="task-detail__ops-col-curr" />
              <col className="task-detail__ops-col-amount" />
              {showCash ? <col className="task-detail__ops-col-num" /> : null}
              {showTax ? <col className="task-detail__ops-col-num" /> : null}
              {showServFee ? <col className="task-detail__ops-col-num" /> : null}
              <col className="task-detail__ops-col-total" />
            </colgroup>
            <thead>
              <tr>
                <th
                  className="task-detail__ops-th task-detail__ops-th--sortable task-detail__ops-th--date"
                  onClick={() => handleSort('operation_date')}
                >
                  Дата{sortMark('operation_date')}
                </th>
                <th
                  className="task-detail__ops-th task-detail__ops-th--sortable task-detail__ops-th--text"
                  onClick={() => handleSort('contractor_name_pf')}
                >
                  Контрагент{sortMark('contractor_name_pf')}
                </th>
                <th
                  className="task-detail__ops-th task-detail__ops-th--sortable task-detail__ops-th--type"
                  onClick={() => handleSort('operation_type')}
                >
                  Тип{sortMark('operation_type')}
                </th>
                <th
                  className="task-detail__ops-th task-detail__ops-th--sortable task-detail__ops-th--text"
                  onClick={() => handleSort('category_name')}
                >
                  Статья ПФ{sortMark('category_name')}
                </th>
                <th
                  className="task-detail__ops-th task-detail__ops-th--sortable task-detail__ops-th--text"
                  onClick={() => handleSort('comment')}
                >
                  Примечание{sortMark('comment')}
                </th>
                <th
                  className="task-detail__ops-th task-detail__ops-th--sortable task-detail__ops-th--curr"
                  onClick={() => handleSort('account_type')}
                >
                  Вал.{sortMark('account_type')}
                </th>
                <th className="task-detail__ops-th task-detail__ops-th--sortable task-detail__ops-th--num" onClick={() => handleSort('amount')}>
                  Сумма{sortMark('amount')}
                </th>
                {showCash ? (
                  <th className="task-detail__ops-th task-detail__ops-th--sortable task-detail__ops-th--num" onClick={() => handleSort('cash_amount')}>
                    Кэш{sortMark('cash_amount')}
                  </th>
                ) : null}
                {showTax ? (
                  <th className="task-detail__ops-th task-detail__ops-th--sortable task-detail__ops-th--num" onClick={() => handleSort('tax_amount')}>
                    Налог{sortMark('tax_amount')}
                  </th>
                ) : null}
                {showServFee ? (
                  <th className="task-detail__ops-th task-detail__ops-th--sortable task-detail__ops-th--num" onClick={() => handleSort('serv_fee_amount')}>
                    Серв. сбор{sortMark('serv_fee_amount')}
                  </th>
                ) : null}
                <th className="task-detail__ops-th task-detail__ops-th--total">
                  Сумма Итого
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredSorted.map((op) => {
                const amount = normalizeOperationAmount(op.amount, op.operation_type);
                const cash = Number(op.cash_amount) || 0;
                const tax = Number(op.tax_amount) || 0;
                const servFee = Number(op.serv_fee_amount) || 0;
                const rowTotal =
                  amount
                  + (showCash ? cash : 0)
                  + (showTax ? tax : 0)
                  + (showServFee ? servFee : 0);
                return (
                  <tr key={op.id}>
                    <td className="task-detail__ops-td task-detail__ops-td--date">
                      {formatDateShort(op.operation_date)}
                    </td>
                    <td className="task-detail__ops-td task-detail__ops-td--text">
                      <span className="task-detail__ops-clamp">{op.contractor_name_pf?.trim() || '—'}</span>
                    </td>
                    <td className="task-detail__ops-td task-detail__ops-td--type">
                      {renderOperationType(op.operation_type)}
                    </td>
                    <td className="task-detail__ops-td task-detail__ops-td--text">
                      <span className="task-detail__ops-clamp">{op.category_name?.trim() || '—'}</span>
                    </td>
                    <td className="task-detail__ops-td task-detail__ops-td--text">
                      <span className="task-detail__ops-clamp">{op.comment?.trim() || '—'}</span>
                    </td>
                    <td className="task-detail__ops-td task-detail__ops-td--curr">
                      {op.account_type?.trim() || '—'}
                    </td>
                    <td className="task-detail__ops-td task-detail__ops-td--amount">
                      {formatNonZeroAmount(amount)}
                    </td>
                    {showCash ? (
                      <td className="task-detail__ops-td task-detail__ops-td--soft-num">
                        {formatNonZeroAmount(cash)}
                      </td>
                    ) : null}
                    {showTax ? (
                      <td className="task-detail__ops-td task-detail__ops-td--soft-num">
                        {formatNonZeroAmount(tax)}
                      </td>
                    ) : null}
                    {showServFee ? (
                      <td className="task-detail__ops-td task-detail__ops-td--soft-num">
                        {formatNonZeroAmount(servFee)}
                      </td>
                    ) : null}
                    <td className="task-detail__ops-td task-detail__ops-td--row-total">
                      {formatNonZeroAmount(rowTotal)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={5} className="task-detail__ops-td task-detail__ops-foot-label">
                  Итого ({filteredSorted.length})
                </td>
                <td className="task-detail__ops-td" />
                <td className="task-detail__ops-td task-detail__ops-td--amount">
                  {formatNonZeroAmount(totals.amount)}
                </td>
                {showCash ? (
                  <td className="task-detail__ops-td task-detail__ops-td--soft-num">
                    {formatNonZeroAmount(totals.cash)}
                  </td>
                ) : null}
                {showTax ? (
                  <td className="task-detail__ops-td task-detail__ops-td--soft-num">
                    {formatNonZeroAmount(totals.tax)}
                  </td>
                ) : null}
                {showServFee ? (
                  <td className="task-detail__ops-td task-detail__ops-td--soft-num">
                    {formatNonZeroAmount(totals.servFee)}
                  </td>
                ) : null}
                <td className="task-detail__ops-td task-detail__ops-td--row-total">
                  {formatNonZeroAmount(totals.total)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
};

export default TaskOperationsTable;
